import { CalendarClock, CheckSquare, Square } from "@tamagui/lucide-icons";
import type { Timestamp } from "firebase/firestore";
import { action, computed, makeObservable, observable, override } from "mobx";
import { TKey } from "../../../shared/translations/translationKeys";
import { ActivityEventType } from "../../activities/types";
import type { Api } from "../../core/apis";
import { showConfetti } from "../../core/states.ts/showConfetti";
import type { Stores } from "../../core/stores";
import BaseModuleModel, {
  type ModuleModelData,
  type Snapshot,
} from "../core/BaseModuleModel";
import {
  ReminderModuleBlockUI,
  ReminderModuleSetupUI,
  ReminderModuleUI,
} from "./ReminderModuleUI";

export type ReminderModuleSettings = {
  offsetInDays?: number;
};

export type ReminderModuleSnapshot = {
  dateDue: Timestamp;
  excludedUsers?: string[];
  completedBy?: string[];
};

export interface ReminderModuleModelData
  extends ModuleModelData<ReminderModuleSnapshot> {
  text: string;
  dateDue: Timestamp;
  excludedUsers: string[];
  completedBy: string[];
  settings: ReminderModuleSettings | null;
}

export default class ReminderModuleModel extends BaseModuleModel<
  ReminderModuleSnapshot,
  ReminderModuleModelData
> {
  constructor(stores: Stores, api: Api, data: ReminderModuleModelData) {
    super(stores, api, data);

    Object.assign(this.data, {
      text: data.text ?? "",
      dateDue: data.dateDue ?? null,
      excludedUsers: data.excludedUsers ?? [],
      completedBy: data.completedBy ?? [],
      settings: data.settings ?? null,
    });

    makeObservable(this);
  }

  // Used to show setting
  hasHelperGuide = false;

  @action async updateSnapshotWithoutSave() {
    this.data.snapshot = {
      dateDue: this.data.dateDue,
      excludedUsers: this.data.excludedUsers,
      completedBy: this.data.completedBy,
    };
  }

  updateModuleSnapshot() {
    this.updateSnapshotWithoutSave();
    this.api.modulesApi.update(this.uid, {
      snapshot: this.data.snapshot,
    });
  }

  @observable showFormSheet = false;

  @action toggleFormSheet() {
    this.showFormSheet = !this.showFormSheet;
    this.setActivitySheetOpen(!this.showFormSheet);
  }

  @computed get infoRowItems() {
    const completedBy = this.data.completedBy;

    const notCompletedBy = this.plan.users?.filter((user) => {
      return (
        !this.data.excludedUsers.includes(user.uid) &&
        !completedBy?.includes(user.uid)
      );
    });

    const items = [];

    if (completedBy.length) {
      items.push({
        icon: CheckSquare,
        text: completedBy
          .map((uid) => {
            return (
              this.stores.usersStore.byId.get(uid)?.data.displayName ??
              "Unknown"
            );
          })
          .join(", "),
      });
    }

    if (notCompletedBy.length) {
      items.push({
        icon: Square,
        text: notCompletedBy
          .map((user) => {
            return user.data.displayName ?? "Unknown";
          })
          .join(", "),
      });
    }

    return items;
  }

  @action async updateDate(dateDue: Timestamp) {
    this.data.dateDue = dateDue;

    this.updateSnapshotWithoutSave();
    this.save();
    this.sendTelemetryEvent("updated_dates");

    this.addEmailReminders(
      this.plan.uid,
      this.plan.data.title,
      this.uid,
      this.data.dateDue,
    );
  }

  @action async toggleEveryoneCompleted() {
    const allCompleted =
      this.activeUsers.length === this.data.completedBy.length;

    if (allCompleted) {
      this.data.completedBy = [];
    } else {
      this.data.completedBy = this.activeUsers.map((user) => user.uid);

      showConfetti();
    }

    this.postActivityEvent(
      ActivityEventType.DataChanged,
      !allCompleted
        ? TKey.ActivityReminderCompleted
        : TKey.ActivityReminderReset,
      {},
    );

    this.updateTimestampWithoutSave();
    this.updateSnapshotWithoutSave();
    this.save();
  }

  @action async toggleCompletedByUser(userUid: string) {
    const isExcluded = this.data.excludedUsers.includes(userUid);

    if (isExcluded) {
      return;
    }

    const completed = !this.data.completedBy.includes(userUid);
    if (completed) {
      this.data.completedBy.push(userUid);

      if (this.isCompleted) {
        showConfetti();
      }
    } else {
      this.data.completedBy = this.data.completedBy.filter(
        (uid) => uid !== userUid,
      );
    }

    this.postActivityEvent(
      ActivityEventType.DataChanged,
      completed ? TKey.ActivityReminderCompleted : TKey.ActivityReminderReset,
      {},
    );

    this.updateTimestampWithoutSave();
    this.updateSnapshotWithoutSave();
    this.save();
  }

  @action async toggleExcluded(userUid: string) {
    const alreadyExcluded = this.data.excludedUsers.includes(userUid);
    if (alreadyExcluded) {
      this.data.excludedUsers = this.data.excludedUsers.filter(
        (uid) => uid !== userUid,
      );
    } else {
      this.data.excludedUsers.push(userUid);

      this.data.completedBy = this.data.completedBy.filter(
        (uid) => uid !== userUid,
      );
    }

    this.updateTimestampWithoutSave();
    this.updateSnapshotWithoutSave();
    this.save();
  }

  setText = async (text: string) => {
    const oldValue = this.data.text;
    if (oldValue !== text) {
      this.updateTimestampWithoutSave();
      this.data.text = text;
      await this.api.modulesApi.set(this);

      this.postActivityEvent(
        ActivityEventType.DataChanged,
        TKey.ActivityReminderDescriptionUpdated,
        {
          oldValue,
          newValue: this.data.text,
        },
      );

      this.updateModuleSnapshot();
      this.sendTelemetryEvent("text_updated");
    }
  };

  @computed
  get activeUsers() {
    return this.plan.users.filter(
      (user) => !this.data.excludedUsers.includes(user.uid),
    );
  }

  @override get isDone() {
    const users = this.activeUsers;
    return this.data.completedBy.length >= users.length;
  }

  @computed get isCompletedByUser() {
    if (!this.userUid) {
      return false;
    }

    return this.data.completedBy.includes(this.userUid);
  }

  @computed get isCompleted() {
    return this.isDone;
  }

  @computed get completedStatus() {
    return {
      count: this.data.completedBy.length,
      total: this.activeUsers.length,
    };
  }

  render() {
    return <ReminderModuleUI module={this} />;
  }

  renderStatic() {
    return null;
  }

  renderBlock() {
    return <ReminderModuleBlockUI module={this} />;
  }

  renderStaticBlock() {
    return this.renderBlock();
  }

  renderIcon(size?: number) {
    return <CalendarClock size={size} />;
  }

  renderBlockSetup = () => {
    return <ReminderModuleSetupUI module={this} />;
  };

  static is(
    module: BaseModuleModel<Snapshot, ModuleModelData<Snapshot>>,
  ): module is ReminderModuleModel {
    return module instanceof ReminderModuleModel;
  }
}
